import { useEffect, useMemo, useState } from 'react'
import { Megaphone, Pin, AlertCircle } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AnnouncementModel } from '@/models/announcement.model'
import { AnnouncementMutationPayload } from '@/services/announcementsService'

export interface AnnouncementFormState {
  title: string
  content: string
  courseId: string | null
  programmeId: string | null
  isPinned: boolean
  isActive: boolean
  sendNotifications: boolean
}

interface CourseOption {
  id: string
  title: string
  programmeId?: string | null
}

interface ProgrammeOption {
  id: string
  title: string
}

interface CreateAnnouncementModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (payload: AnnouncementMutationPayload, announcementId?: string | null) => Promise<void>
  isSubmitting?: boolean
  announcement?: AnnouncementModel | null
  courses?: CourseOption[]
  programmes?: ProgrammeOption[]
}

const defaultState: AnnouncementFormState = {
  title: '',
  content: '',
  courseId: null,
  programmeId: null,
  isPinned: false,
  isActive: true,
  sendNotifications: true,
}

export function CreateAnnouncementModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  announcement,
  courses = [],
  programmes = [],
}: CreateAnnouncementModalProps) {
  const [formData, setFormData] = useState<AnnouncementFormState>(defaultState)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const isEditing = Boolean(announcement)
  const filteredCourses = useMemo(() => {
    if (!formData.programmeId) return courses
    return courses.filter((course) => course.programmeId === formData.programmeId)
  }, [courses, formData.programmeId])

  useEffect(() => {
    if (!isOpen) return
    if (announcement) {
      setFormData({
        title: announcement.title ?? '',
        content: announcement.content ?? '',
        courseId: announcement.courseId ?? null,
        programmeId: announcement.programmeId ?? null,
        isPinned: announcement.isPinned,
        isActive: announcement.isActive,
        sendNotifications: true,
      })
    } else {
      setFormData(defaultState)
    }
    setErrors({})
    setHasUnsavedChanges(false)
  }, [isOpen, announcement])

  const isCourseSpecific = useMemo(() => Boolean(formData.courseId), [formData.courseId])

  useEffect(() => {
    if (!formData.courseId) return
    const stillMatches = filteredCourses.some((course) => course.id === formData.courseId)
    if (!stillMatches) {
      setFormData((prev) => ({ ...prev, courseId: null }))
    }
  }, [filteredCourses, formData.courseId])

  const validateForm = (): boolean => {
    const nextErrors: Record<string, string> = {}
    if (!formData.title.trim()) {
      nextErrors.title = 'Announcement title is required'
    }
    if (!formData.content.trim()) {
      nextErrors.content = 'Please provide the main message'
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleInputChange = (field: keyof AnnouncementFormState, value: string | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setHasUnsavedChanges(true)
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const handleClose = () => {
    if (hasUnsavedChanges && (formData.title || formData.content)) {
      const confirmClose = window.confirm('You have unsaved changes. Close the editor?')
      if (!confirmClose) {
        return
      }
    }
    onClose()
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validateForm()) return

    await onSubmit(
      {
        title: formData.title.trim(),
        content: formData.content.trim(),
        courseId: formData.courseId || undefined,
        programmeId: formData.programmeId || undefined,
        isPinned: formData.isPinned,
        isActive: formData.isActive,
        sendNotifications: formData.sendNotifications,
      },
      announcement?.id ?? null,
    )
    setHasUnsavedChanges(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title={isEditing ? 'Update announcement' : 'Create announcement'}
    >
      <div className="max-h-[90vh] overflow-y-auto">
        {hasUnsavedChanges && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
            <AlertCircle className="h-4 w-4" />
            Unsaved changes
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <Megaphone className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900">
                  {isEditing ? 'Edit announcement' : 'New announcement'}
                </h3>
                <p className="text-sm text-secondary-500">
                  Share timely updates with learners. Leave course unselected for general updates.
                </p>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-secondary-700">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.title}
                onChange={(event) => handleInputChange('title', event.target.value)}
                placeholder="System maintenance notice"
                className={errors.title ? 'border-red-500' : ''}
              />
              {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-secondary-700">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.content}
                onChange={(event) => handleInputChange('content', event.target.value)}
                rows={6}
                placeholder="Provide context, deadlines, or links learners should know…"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.content ? 'border-red-500' : 'border-secondary-300'
                }`}
              />
              {errors.content && <p className="mt-1 text-sm text-red-500">{errors.content}</p>}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-secondary-900">Audience</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-secondary-700">Programme (optional)</label>
                <select
                  value={formData.programmeId || ''}
                  onChange={(event) => handleInputChange('programmeId', event.target.value || null)}
                  className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All programmes</option>
                  {programmes.map((programme) => (
                    <option key={programme.id} value={programme.id}>
                      {programme.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-secondary-700">Course (optional)</label>
                <select
                  value={formData.courseId || ''}
                  onChange={(event) => handleInputChange('courseId', event.target.value || null)}
                  className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">General announcement</option>
                  {filteredCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {isCourseSpecific && (
              <p className="rounded-md border border-primary-100 bg-primary-50 px-3 py-2 text-xs text-primary-700">
                Learners enrolled in the selected course will receive this update.
              </p>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-secondary-900">Delivery</h3>
            <div className="space-y-3 rounded-lg border border-secondary-200 bg-secondary-50 p-4 text-sm text-secondary-700">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  checked={formData.isPinned}
                  onChange={(event) => handleInputChange('isPinned', event.target.checked)}
                />
                <span className="flex items-center gap-1 font-medium">
                  <Pin className="h-4 w-4" />
                  Pin announcement
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  checked={formData.sendNotifications}
                  onChange={(event) => handleInputChange('sendNotifications', event.target.checked)}
                />
                <span>
                  Email enrolled learners
                  <span className="block text-xs text-secondary-500">Sends notification immediately</span>
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  checked={formData.isActive}
                  onChange={(event) => handleInputChange('isActive', event.target.checked)}
                />
                <span>Announcement is active</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t pt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : isEditing ? 'Save changes' : 'Create announcement'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
